// src/app/rail-popover-anchoring.ts
// Shared anchoring for every popover opened from the PractitionerNav rail — the account
// menu and the notifications inbox. Both open BESIDE their tile, level with it, and both
// must clear the rail rather than sit on top of it. The numbers live in one place instead
// of being typed twice with a drift risk.
//
// Geometry, all in CSS px (the app's root `zoom: 0.9` scales the rendered result, it does
// not change these):
//
//   rail width                                     56   (DS PractitionerNav, fixed on web; folded)
//   tile width                                     40   (showcase bridge: `--rail-row-height`)
//   tile inset inside the rail  (56 − 40) / 2  =    8   (RailAction is `mx-auto`)
//   tile right edge             8 + 40        =   48
//
// `side="right"` measures `sideOffset` from the TILE's right edge, which sits 8px inside
// the rail. So clearing the rail by a real gap needs the offset to absorb that overhang:
//
//   sideOffset = (56 − 48) + 8 = 16
//
// which puts the surface's left edge 8px clear of the rail's right edge.
//
// With the labels open (232px, rail-state.ts) the anchor host spans the rail's inner width (216, 8
// inside each edge), so its right edge is 8 inside the rail again and the same offset clears the
// rail by the same 8px.
//
// `align="end"` puts the surface's BOTTOM edge level with the tile's bottom edge, so it
// reads as level with its trigger and grows upward. `center` is wrong here: the account
// tile is the last thing in the rail, and centring a ~190px surface on a 48px tile at the
// bottom of the viewport pushes half of it off-screen.

/** DS PractitionerNav rail width on web, CSS px. */
const RAIL_WIDTH = 56;
/** Rail tile width, CSS px (the showcase draws the DS RailAction at 40, design log 12 Oct). */
const TILE_WIDTH = 40;
/** Breathing room between the rail and any surface opened from it, CSS px (`--spacing-2`). */
const RAIL_GAP = 8;
/** How far the tile's right edge sits inside the rail, CSS px. */
const TILE_OVERHANG = (RAIL_WIDTH - TILE_WIDTH) / 2;

/** Rail popovers open to the side of their tile, not above it. */
export const RAIL_POPOVER_SIDE = 'right' as const;

/** Bottom-aligned with the tile: level with the trigger, growing upward. */
export const RAIL_POPOVER_ALIGN = 'end' as const;

/**
 * Distance from the tile's right edge, chosen so the surface clears the RAIL by `RAIL_GAP`.
 * Derived, not tuned by eye — see the note above.
 */
export const RAIL_POPOVER_SIDE_OFFSET = TILE_OVERHANG + RAIL_GAP;
